import React, { useState } from 'react';
import { publicUrl, thumbUrl } from '../lib/fileUrl';
import { isPdf, isVideo } from './MediaStrip';
import { displayLabel } from '../lib/ledger';
import { fmtDay } from '../lib/format';
import { vendorDocuments } from '../lib/vendorDocs';

// ── Everything this technician has actually sent in ────────────────────────
//
// The vendor panel listed the paperwork as STATUS: four pills saying valid,
// expiring, missing. What it never showed was the documents themselves. The
// insurance certificate, the licence, the W-9 — all uploaded, all on the
// record, none of them on the screen. Julian could see that insurance existed
// and could not look at it.
//
// So: a tile per document, with what it is, who issued it and when it runs
// out, opening full size on a tap. A picture gets a thumbnail of itself; a PDF
// gets a document tile, because rendering the first page of a 3MB inspection
// report to draw a 72-pixel square is a download nobody asked for.
//
// Which documents, and where they come from, is in lib/vendorDocs.js — three
// different upload paths land in three different shapes on the record, and
// this screen used to draw two of them.
export default function VendorDocuments({ vendor, onOpenW9 }) {
  const [broken, setBroken] = useState({});

  const docs = vendorDocuments(vendor, displayLabel, fmtDay);

  // A W-9 signed in the app is not a file — it is a form with a drawn
  // signature, and it opens in its own viewer. Named here anyway, because a
  // grid that says "Documents they have sent" and leaves out the W-9 reads
  // as a W-9 nobody ever did.
  const signedW9 = vendor?.w9?.signedAt
    && !(vendor?.profile?.w9Url)
    ? vendor.w9 : null;

  if (docs.length === 0 && !signedW9) {
    return (
      <div className="vdocs">
        <div className="vdocs-h">Documents they have sent</div>
        <div className="vdocs-none">
          Nothing uploaded yet. They add these from My Documents in their own
          portal, or you can add one with the button above.
        </div>
      </div>
    );
  }

  const open = (u) => () => window.open(u, '_blank', 'noopener');

  return (
    <div className="vdocs">
      <div className="vdocs-h">Documents they have sent ({docs.length + (signedW9 ? 1 : 0)})</div>
      <div className="vdocs-grid">
        {docs.map((d, i) => {
          const u = publicUrl(d.url);
          const asImage = !isPdf(u) && !isVideo(u) && !broken[i];
          return (
            <button key={i} type="button" className="vdoc" onClick={open(u)} title={`${d.title} — stored as: ${d.url}`}>
              <span className="vdoc-thumb">
                {asImage ? (
                  <img src={thumbUrl(d.url)} alt="" loading="lazy"
                    onError={() => setBroken((b) => ({ ...b, [i]: true }))} />
                ) : (
                  <span className="vdoc-ext">{extOf(u)}</span>
                )}
              </span>
              <span className="vdoc-title">{d.title}</span>
              {d.sub && <span className="vdoc-sub">{d.sub}</span>}
              <span className="vdoc-note">{d.note}</span>
            </button>
          );
        })}

        {signedW9 && (
          <button
            type="button"
            className="vdoc"
            onClick={onOpenW9}
            disabled={!onOpenW9}
            title="The W-9 they signed in the app"
          >
            <span className="vdoc-thumb">
              <span className="vdoc-ext">W-9</span>
            </span>
            <span className="vdoc-title">W-9</span>
            <span className="vdoc-sub">{signedW9.name}</span>
            <span className="vdoc-note">Signed {fmtDay(signedW9.signedAt)}</span>
          </button>
        )}
      </div>
    </div>
  );
}

// A Cloudinary URL ends in a random id, so the extension is the only thing
// that says what a reader is about to open.
const extOf = (url) => {
  const m = String(url || '').match(/\.([a-z0-9]{2,5})(?:\?|$)/i);
  return m ? m[1].toUpperCase() : 'FILE';
};
